import React, { useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
  Filler
} from 'chart.js';
import { useBrandBranches, useBranchSummaryData } from '../hooks/useBrandQueries';
import { useBranch } from '../contexts/BranchContext';
import { useTimeFrame } from '../contexts/TimeFrameContext';
import LicenseGate from '../components/ui/LicenseGate';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import ErrorMessage from '../components/ui/ErrorMessage';
import EmptyState from '../components/ui/EmptyState';
import { logger } from '../utils/logger';

// Import brand comparison components
import { 
  SimpleComparisonTab,
  AdvancedComparisonTab
} from '../components/brand';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  ChartTooltip,
  ChartLegend,
  Filler
);

/**
 * Props for the BranchComparisonDashboard component
 */
interface BranchComparisonDashboardProps {
  /** Optional brand ID to override context value */
  brandId?: number;
  /** Optional time range to override context value (e.g., 'last6Months', 'last12Months') */
  timeRange?: string;
}

/**
 * Available metrics for branch comparison
 * Organized by category: financial, customer, performance
 */
export const AVAILABLE_METRICS = [
  { id: 'revenue', label: 'فروش', unit: 'currency', category: 'financial' },
  { id: 'customerCount', label: 'تعداد مشتریان', unit: 'number', category: 'customer' },
  { id: 'avgTransactionValue', label: 'میانگین تراکنش', unit: 'currency', category: 'financial' },
  { id: 'subscription', label: 'اشتراک‌گیری', unit: 'percentage', category: 'performance' },
  { id: 'creditInvoiceCount', label: 'مجموع تعداد سفارش‌های با اعتبار', unit: 'number', category: 'financial' },
  { id: 'creditInvoiceAmount', label: 'مجموع مبلغ سفارش‌های با اعتبار', unit: 'currency', category: 'financial' },
  { id: 'usedCredit', label: 'مجموع مبلغ اعتبار مصرفی', unit: 'currency', category: 'financial' },
  { id: 'clubMembershipRate', label: 'نرخ عضویت باشگاه', unit: 'percentage', category: 'performance' },
  { id: 'satisfactionScore', label: 'امتیاز رضایت', unit: 'score', category: 'performance' },
  { id: 'invoiceCount', label: 'تعداد سفارش', unit: 'number', category: 'financial' },
  { id: 'uniqueCustomers', label: 'مشتریان یکتا', unit: 'number', category: 'customer' },
  { id: 'repeatCustomerRate', label: 'نرخ مشتریان تکراری', unit: 'percentage', category: 'customer' },
  { id: 'avgOrderValue', label: 'میانگین ارزش سفارش', unit: 'currency', category: 'financial' },
  { id: 'customerRetentionRate', label: 'نرخ حفظ مشتری', unit: 'percentage', category: 'customer' },
  { id: 'newCustomerRate', label: 'نرخ مشتریان جدید', unit: 'percentage', category: 'customer' },
  { id: 'creditUsageRate', label: 'نرخ استفاده از اعتبار', unit: 'percentage', category: 'performance' },
  { id: 'memberCount', label: 'تعداد اعضای باشگاه', unit: 'number', category: 'customer' },
  { id: 'growthRate', label: 'نرخ رشد', unit: 'percentage', category: 'performance' },
  { id: 'churnRate', label: 'نرخ ریزش', unit: 'percentage', category: 'performance' },
] as const;

const getFirstNumericValue = (
  data: Record<string, unknown>,
  fieldNames: string[],
  fallback = 0
): number => {
  for (const fieldName of fieldNames) {
    const value = data[fieldName];
    if (typeof value === 'number' && !Number.isNaN(value)) {
      return value;
    }
  }

  return fallback;
};

/**
 * Branch Comparison Dashboard Component
 * 
 * Allows users to select 2-6 branches and compare them across multiple metrics.
 * Displays comparison data in both table and chart formats with CSV export capability.
 * 
 * Features:
 * - Multi-select branch selector (2-6 branches)
 * - Multi-select metric selector (15 available metrics)
 * - Comparison table with highlighting (highest/lowest values)
 * - Comparison charts (one per metric)
 * - CSV export functionality
 * - Debounced API calls (300ms) for performance
 * - Currency normalization for accurate comparisons
 * - Loading and error states
 * - Empty state when insufficient selections
 * - Accessibility support (ARIA labels, keyboard navigation)
 * - Responsive design (mobile, tablet, desktop)
 * 
 * @component
 * @example
 * ```tsx
 * <BranchComparisonDashboard brandId={123} timeRange="last6Months" />
 * ```
 */
const BranchComparisonDashboard: React.FC<BranchComparisonDashboardProps> = ({
  brandId: propBrandId,
  timeRange: propTimeRange
}) => {
  // Get context values
  const { selectedBranch } = useBranch();
  const { timeFrame } = useTimeFrame();
  
  // Use prop values or fall back to context
  const brandId = propBrandId || selectedBranch?.brandId || null;
  const timeRange = propTimeRange || timeFrame || 'last6Months';
  
  // Tab state: 'simple' or 'advanced'
  const [activeTab, setActiveTab] = useState<'simple' | 'advanced'>('simple');
  
  // Fetch branches for the brand
  const { data: branches = [], isLoading: branchesLoading, error: branchesError } = useBrandBranches(brandId);
  
  // Fetch summary data for both tabs (contains all branches and metrics)
  const {
    data: summaryData,
    isLoading: summaryLoading,
    error: summaryError,
    refetch: refetchSummary
  } = useBranchSummaryData(brandId, timeRange);
  
  // Handle error logging
  React.useEffect(() => {
    if (branchesError) {
      logger.error('Failed to load branches', 'BranchComparisonDashboard', branchesError);
    }
    if (summaryError) {
      logger.error('Failed to load summary data', 'BranchComparisonDashboard', summaryError);
    }
  }, [branchesError, summaryError]);
  
  // Show error state for branches
  if (branchesError) {
    return (
      <LicenseGate licenseType="club" dashboardName="مقایسه شعب">
        <div className="container-dashboard" dir="rtl">
          <h1 className="title-dashboard">مقایسه شعب</h1>
          <ErrorMessage
            message="خطا در بارگذاری لیست شعب"
            onRetry={() => window.location.reload()}
          />
        </div>
      </LicenseGate>
    );
  }
  
  // Show loading state for branches
  if (branchesLoading) {
    return (
      <LicenseGate licenseType="club" dashboardName="مقایسه شعب">
        <div className="container-dashboard" dir="rtl">
          <h1 className="title-dashboard">مقایسه شعب</h1>
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <LoadingSpinner
              variant="minimal"
              size="lg"
              message="در حال بارگذاری لیست شعب…"
            />
          </div>
        </div>
      </LicenseGate>
    );
  }
  
  return (
    <LicenseGate licenseType="club" dashboardName="مقایسه شعب">
      <div className="container-dashboard">
        {/* Skip to main content link for keyboard users */}
        <a
          href="#branch-comparison-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:right-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:shadow-lg"
        >
          پرش به محتوای اصلی
        </a>
        
        <div className="space-y-8 md:space-y-10 animate-enter-up">
          {/* Dashboard title */}
          <h1 className="title-dashboard">
            مقایسه شعب
          </h1>
{/*           
          <div className="card-base card-padding">
            <div className="flex gap-2 border-b border-border-default -mx-6 -mt-6 px-6 pb-0">
              <button
                onClick={() => setActiveTab('simple')}
                className={`px-6 py-3 text-base font-medium transition-all duration-base relative min-h-[44px] ${
                  activeTab === 'simple'
                    ? 'text-primary-600 border-b-2 border-primary-600'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
                aria-selected={activeTab === 'simple'}
                role="tab"
              >
                مقایسه ساده
              </button>
            </div>
          </div> */}
          
          {/* Tab Content */}
          <div id="branch-comparison-content">
            {activeTab === 'simple' ? (
              /* Simple Tab - shows 4 key metrics for all branches */
              <>
                {summaryError && (
                  <section role="alert" aria-live="assertive">
                    <ErrorMessage
                      message="خطا در بارگذاری داده‌های مقایسه"
                      onRetry={() => refetchSummary()}
                    />
                  </section>
                )}
                
                {summaryLoading && (
                  <section aria-live="polite" aria-busy="true">
                    <div className="flex flex-col items-center justify-center min-h-[300px]">
                      <LoadingSpinner
                        variant="minimal"
                        size="lg"
                        message="در حال بارگذاری داده‌های مقایسه…"
                      />
                    </div>
                  </section>
                )}
                
                {!summaryLoading && !summaryError && summaryData && summaryData.branches.length > 0 && (
                  <SimpleComparisonTab
                    branches={summaryData.branches.map(b => ({
                      branchId: b.branchId,
                      branchName: b.branchName,
                      currency: b.currency,
                      metrics: {
                        revenue: b.currentMonthRevenue || 0,
                        invoiceCount: b.invoiceCount || 0,
                        customerCount: b.customerCount || 0,
                        satisfactionScore: b.satisfactionScore || 0,
                        subscription: getFirstNumericValue(
                          b as unknown as Record<string, unknown>,
                          ['subscription', 'subscriptionRate', 'subscription_percentage']
                        ),
                        creditInvoiceCount: getFirstNumericValue(
                          b as unknown as Record<string, unknown>,
                          ['creditInvoiceCount', 'validCreditInvoiceCount']
                        ),
                        creditInvoiceAmount: getFirstNumericValue(
                          b as unknown as Record<string, unknown>,
                          ['creditInvoiceAmount', 'validCreditInvoiceAmount']
                        ),
                        usedCredit: getFirstNumericValue(
                          b as unknown as Record<string, unknown>,
                          ['usedCredit', 'usedCreditAmount', 'sumUsedCredit']
                        ),
                      }
                    }))}
                    metrics={[
                      'revenue',
                      'invoiceCount',
                      'customerCount',
                      'satisfactionScore',
                      'subscription',
                      'creditInvoiceCount',
                      'creditInvoiceAmount',
                      'usedCredit'
                    ].map(id => AVAILABLE_METRICS.find(m => m.id === id)!).filter(Boolean)}
                    metricAverages={summaryData.metricAverages}
                    loading={summaryLoading}
                  />
                )}
                
                {!summaryLoading && !summaryError && (!summaryData || summaryData.branches.length === 0) && (
                  <section aria-live="polite" aria-atomic="true">
                    <EmptyState
                      message="برای این انتخاب داده‌ای ثبت نشده — شعبه یا بازه‌ی زمانی دیگری را امتحان کنید"
                      icon="📊"
                    />
                  </section>
                )}
              </>
            ) : (
              /* Advanced Tab - shows all branches and all metrics */
              <>
                {summaryError && (
                  <section role="alert" aria-live="assertive">
                    <ErrorMessage
                      message="خطا در بارگذاری داده‌های مقایسه"
                      onRetry={() => refetchSummary()}
                    />
                  </section>
                )}
                
                {summaryLoading && (
                  <section aria-live="polite" aria-busy="true">
                    <div className="flex flex-col items-center justify-center min-h-[300px]">
                      <LoadingSpinner
                        variant="minimal"
                        size="lg"
                        message="در حال بارگذاری داده‌های مقایسه…"
                      />
                    </div>
                  </section>
                )}
                
                {!summaryLoading && !summaryError && summaryData && (
                  <AdvancedComparisonTab
                    branches={summaryData.branches}
                    allMetrics={AVAILABLE_METRICS as any}
                    loading={summaryLoading}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </LicenseGate>
  );
};

export default BranchComparisonDashboard;
